import { SecondaryButton, SelectButton } from '@magoosh/lib/button';
import FieldRadioGroup from '@magoosh/lib/formik_inputs/field_radio_group';
import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Alert } from 'react-bootstrap';
import * as yup from 'yup';
import fetch from 'utilities/fetch';
import { paths } from 'config/path_helpers';
import { Sections } from '@magoosh/gre/assets/icons';
import ContextualLink from '@magoosh/gre/app/contextual_link';

interface Props {
  studyPlanItemId: number;
}
interface MixedPracticeOptions {
  customizeUrl: string;

  section: Sections;

  itemCountValues: number[];
  itemCountDefault: number;

  practiceModes: { label: string; value: PracticeMode }[];
  practiceModeDefault: PracticeMode;

  recommendedTopics: string;
}

type PracticeMode = 'untimed_practice' | 'timed_practice' | 'untimed_quiz' | 'timed_quiz';

const validationSchema = yup.object({
  mode: yup.string().required('Please select a practice mode').label('Practice Mode'),
  itemCount: yup.number().required('Please select a question limit').label('Question Limit'),
  includeRecommendedTopics: yup.boolean().required('Please select a topic option.').label('Topics')
});

export const MixedPracticeForm: React.FC<Props> = (props) => {
  const [options, setOptions] = useState<MixedPracticeOptions>();

  useEffect(() => {
    fetch(paths.api.mixedPracticeOptions(props.studyPlanItemId)).then(setOptions);
  }, [props.studyPlanItemId]);

  if (!options) {
    return null;
  }

  return (
    <Formik
      initialValues={{
        mode: options.practiceModeDefault,
        itemCount: options.itemCountDefault,
        includeRecommendedTopics: true
      }}
      onSubmit={(values, { setSubmitting, setErrors, setFieldError }) => {
        setSubmitting(true);
        fetch(paths.api.mixedPractice, {
          body: JSON.stringify({
            mode: values.mode,
            itemCount: values.itemCount,
            useRecommendedTopics: values.includeRecommendedTopics,
            studyPlanItemId: props.studyPlanItemId
          }),
          method: 'POST'
        })
          .then((result) => {
            window.location.assign(result.practiceSessionPath);
          })
          .catch(({ errors }) => {
            setErrors(errors);
            if (errors._error) {
              setFieldError('general', errors._error);
            }
          })
          .finally(() => {
            setSubmitting(false);
          });
      }}
      validationSchema={validationSchema}>
      {({ isSubmitting, handleSubmit, values, errors, setFieldValue }) => (
        <Form>
          <hr className="u-margin-T-n" />
          {errors['general'] && <Alert bsStyle="danger">{errors['general']}</Alert>}

          {options.section !== Sections.Writing && (
            <>
              <FieldRadioGroup
                label={<h5><strong>Topics</strong></h5>}
                name="includeRecommendedTopics"
                description=""
                value={values.includeRecommendedTopics}
                optionsArray={[
                  {
                    value: true,
                    label: 'Recommended Topics',
                    hint: 'Including ' + options.recommendedTopics
                  },
                  { value: false, label: `All ${options.section.toLowerCase()} topics` }
                ]}
              />

              <hr />

              <h5>
                <strong>Number of Questions</strong>
              </h5>
              <div>
                {options.itemCountValues.map((count) => (
                  <SelectButton
                    key={count}
                    onClick={() => setFieldValue('itemCount', count)}
                    disabled={values.itemCount == count}>
                    {count}
                  </SelectButton>
                ))}
              </div>
            </>
          )}

          {options.section == Sections.Writing && (
            <p className="u-margin-B-m">You'll practice one essay prompt in this session.</p>
          )}

          <hr />

          <h5>
            <strong>Mode</strong>
          </h5>

          <FieldRadioGroup
            name="mode"
            optionsArray={options.practiceModes}
            value={values.mode}
          />

          <div style={{ verticalAlign: 'middle' }} className="u-margin-T-m">
            <SecondaryButton submitting={isSubmitting} disabled={!options} onClick={handleSubmit} className="u-margin-R-s">
              Start Session
            </SecondaryButton>

            <ContextualLink
              href={options.customizeUrl}
              style={{ color: '#4E4B50' }}>
              More Customization
            </ContextualLink>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default MixedPracticeForm;
